/**
 * Profession feedback — the client-only, cosmetic layer that turns the owner-only S_PROFESSIONS
 * stream into felt progress: a discreet corner "+N XP 🎣 Fisher" toast (aggregated across bursts),
 * a celebratory level-up banner with the live perk value, and the localStorage-backed "unseen
 * level-up" state that lights the 🧰 badge. NOTHING here touches the wire or the sim — it only
 * reads `net.professions` (already decoded) and the shared level/perk maths from @piratas/protocol.
 *
 * `diffProfessions` / `unseenLevelUps` are pure (no DOM, no storage) so they can be unit-tested.
 */
import { PROFESSION_KEYS, PROFESSIONS, professionLevel, professionPerkFactor } from '@piratas/protocol';
import type { Audio } from './audio.js';
import { itemIcon } from './itemicon.js';

export interface ProfGain { track: string; delta: number; }
export interface ProfLevelUp { track: string; level: number; }
export interface ProfDiff { gains: ProfGain[]; levelUps: ProfLevelUp[]; }

/** Pure diff of two profession XP snapshots: per-track XP gains and (a subset of) level crossings. */
export function diffProfessions(prev: Record<string, number>, next: Record<string, number>): ProfDiff {
  const gains: ProfGain[] = [];
  const levelUps: ProfLevelUp[] = [];
  for (const k of PROFESSION_KEYS) {
    const a = prev[k] ?? 0, b = next[k] ?? 0;
    if (b > a) {
      gains.push({ track: k, delta: Math.round(b - a) });
      const la = professionLevel(a), lb = professionLevel(b);
      if (lb > la) levelUps.push({ track: k, level: lb });
    }
  }
  return { gains, levelUps };
}

/** Tracks whose CURRENT level exceeds the last level the player acknowledged (seen[k], default lv1). */
export function unseenLevelUps(professions: Record<string, number>, seen: Record<string, number>): string[] {
  const out: string[] = [];
  for (const k of PROFESSION_KEYS) {
    if (professionLevel(professions[k] ?? 0) > (seen[k] ?? 1)) out.push(k);
  }
  return out;
}

/** The current level of every track (used to seed / update the "seen" record). */
export function currentLevels(professions: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of PROFESSION_KEYS) out[k] = professionLevel(professions[k] ?? 0);
  return out;
}

/** Map-UX item 6 (owner: "o aviso de XP tá batendo em cima do minimapa"): the toast's top offset given the
 *  stylesheet's own resolved top and the chart's LIVE bottom edge (null when the chart isn't on screen).
 *  Pure so the anchoring rule is unit-testable: never above the CSS slot, never on the chart. */
export function xpToastTop(cssTop: number, chartBottom: number | null, gap = 10): number {
  if (chartBottom === null || !Number.isFinite(chartBottom)) return cssTop;
  return Math.max(cssTop, Math.round(chartBottom + gap));
}

/* ---- localStorage: the persistent "last level seen per profession" (survives refresh) ---- */
const SEEN_KEY = 'sor:profSeen';
/** Returns the stored seen-levels, or `null` if the player has never had them seeded. */
export function loadSeenLevels(): Record<string, number> | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(SEEN_KEY) : null;
    return raw ? (JSON.parse(raw) as Record<string, number>) : null;
  } catch { return null; }
}
export function saveSeenLevels(levels: Record<string, number>): void {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(SEEN_KEY, JSON.stringify(levels)); } catch { /* private mode / quota — non-fatal */ }
}

/** A short live-perk line, e.g. "Sail speed +9%", derived from the catalog perk + the current factor. */
export function perkLine(track: string, xp: number): string {
  const pct = (professionPerkFactor(xp) - 1) * 100;
  const raw = PROFESSIONS[track]?.perk ?? '';
  const suffix = raw.replace(/^up to \+\d+%\s*/i, '') || 'bonus';
  const label = suffix.charAt(0).toUpperCase() + suffix.slice(1);
  return `${label} +${pct >= 1 ? Math.round(pct) : pct.toFixed(1)}%`;
}

const SERIF = "'Iowan Old Style','Palatino Linotype','Book Antiqua',Palatino,Georgia,'Times New Roman',serif";
const CSS = `
/* below the chart, never on it — the owner's screenshot had the XP pill parked over the minimap */
#profxp { position:fixed; right:calc(env(safe-area-inset-right,0px) + 10px); top:calc(env(safe-area-inset-top,8px) + 188px); z-index:8; pointer-events:none;
  max-width:62vw; padding:7px 13px; border-radius:11px; opacity:0; transform:translateY(-8px); transition:opacity .28s ease, transform .28s ease;
  font-family:${SERIF}; font-size:13.5px; font-weight:800; letter-spacing:.2px; color:#f4e9cf; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  background:linear-gradient(165deg, rgba(16,30,46,.9), rgba(6,14,26,.94)); border:1px solid rgba(232,192,116,.4);
  box-shadow:0 6px 20px rgba(0,8,16,.5), inset 0 1px 0 rgba(255,255,255,.06); }
#profxp.on { opacity:1; transform:none; }
@media (orientation:landscape) and (max-height:540px){ #profxp { top:calc(env(safe-area-inset-top,8px) + 150px); } }
#profxp .xpv { color:#e8c074; }
#proflvl { position:fixed; left:50%; top:19%; transform:translateX(-50%) scale(.7); opacity:0; z-index:9; pointer-events:none; text-align:center;
  padding:12px 30px 13px; border-radius:16px; font-family:${SERIF};
  background:linear-gradient(165deg, rgba(58,42,12,.95), rgba(28,18,4,.96)); border:1.5px solid rgba(255,210,120,.62);
  box-shadow:0 12px 46px rgba(0,0,0,.55), 0 0 40px -4px rgba(255,190,80,.6); }
#proflvl.go { animation:proflvlpop 4s cubic-bezier(.2,.9,.25,1) forwards; }
#proflvl .pl-t { font-size:23px; font-weight:900; letter-spacing:.6px; color:#ffe9b0; text-shadow:0 2px 8px #000; }
#proflvl .pl-p { font-size:14px; font-weight:800; color:#ffd066; margin-top:3px; }
@keyframes proflvlpop { 0%{opacity:0; transform:translateX(-50%) scale(.7);} 7%{opacity:1; transform:translateX(-50%) scale(1.08);}
  14%{transform:translateX(-50%) scale(1);} 88%{opacity:1; transform:translateX(-50%) scale(1);} 100%{opacity:0; transform:translateX(-50%) scale(1) translateY(-12px);} }
`;

const AGG_WINDOW_MS = 2000; // gains arriving inside this window merge into the live toast
const XP_SHOW_MS = 2400;    // how long the XP toast stays up after the latest gain

/**
 * Owns the two transient DOM cues (XP toast + level-up banner). Fed one call per fresh
 * S_PROFESSIONS snapshot via {@link onUpdate}; the very first call of the session is the baseline
 * (no toast — otherwise the login snapshot would spam). Keeps at most one XP toast visible,
 * aggregating rapid bursts into it.
 */
export class ProfNotifier {
  private prev: Record<string, number> | null = null;
  private readonly xpEl: HTMLElement;
  private readonly lvlEl: HTMLElement;
  private readonly agg = new Map<string, number>();
  private aggStart = 0;
  private xpHideTimer = 0;

  constructor(root: HTMLElement, private readonly audio: Audio) {
    const style = document.createElement('style'); style.textContent = CSS; document.head.appendChild(style);
    this.xpEl = document.createElement('div'); this.xpEl.id = 'profxp';
    this.lvlEl = document.createElement('div'); this.lvlEl.id = 'proflvl';
    root.appendChild(this.xpEl); root.appendChild(this.lvlEl);
  }

  /** Call once per fresh S_PROFESSIONS (i.e. whenever net.professionsVersion bumps). */
  onUpdate(professions: Record<string, number>): void {
    const snapshot = { ...professions };
    if (this.prev === null) { this.prev = snapshot; return; } // session baseline — never toast
    const diff = diffProfessions(this.prev, snapshot);
    this.prev = snapshot;
    if (diff.gains.length) this.pushXp(diff.gains);
    const up = diff.levelUps[0];
    if (up) this.showLevelUp(up, snapshot);
  }

  private pushXp(gains: ProfGain[]): void {
    const now = performance.now();
    const active = this.xpEl.classList.contains('on') && (now - this.aggStart) < AGG_WINDOW_MS;
    if (!active) { this.agg.clear(); this.aggStart = now; }
    for (const g of gains) this.agg.set(g.track, (this.agg.get(g.track) ?? 0) + g.delta);
    this.renderXp();
    this.placeXp();
    this.xpEl.classList.add('on');
    clearTimeout(this.xpHideTimer);
    this.xpHideTimer = window.setTimeout(() => this.xpEl.classList.remove('on'), XP_SHOW_MS);
  }

  /** Anchor the toast BELOW the live chart (minimap + its rail/hint), wherever the current breakpoint or a
   *  HUD-Forge custom layout put it — the owner's screenshot had the XP pill parked ON the minimap. Runs
   *  only when a toast shows (never per frame); falls back to the stylesheet slot when the chart is hidden. */
  private placeXp(): void {
    const el = this.xpEl;
    el.style.top = ''; // let the stylesheet speak first (env() safe-areas + media queries resolve here)
    let chartBottom: number | null = null;
    const map = document.getElementById('mapwrap');
    if (map) {
      const r = map.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        chartBottom = r.bottom;
        const hint = map.querySelector('.map-hint'); // absolute child — outside the parent's rect
        if (hint) { const hr = (hint as HTMLElement).getBoundingClientRect(); if (hr.height > 0) chartBottom = Math.max(chartBottom, hr.bottom); }
      }
    }
    const cssTop = Number.parseFloat(getComputedStyle(el).top) || 0;
    const top = xpToastTop(cssTop, chartBottom);
    if (top !== cssTop) el.style.top = `${top}px`;
  }

  private renderXp(): void {
    const entries = [...this.agg.entries()];
    const first = entries[0];
    if (entries.length === 1 && first) {
      const [k, v] = first; const m = PROFESSIONS[k];
      this.xpEl.innerHTML = `<span class="xpv">+${v} XP</span> ${itemIcon(m?.icon ?? '⚓')} ${m?.name ?? k}`;
    } else {
      const total = entries.reduce((s, [, v]) => s + v, 0);
      const icons = entries.map(([k]) => itemIcon(PROFESSIONS[k]?.icon ?? '⚓')).join('');
      this.xpEl.innerHTML = `<span class="xpv">+${total} XP</span> · ${entries.length} trades ${icons}`;
    }
  }

  private showLevelUp(up: ProfLevelUp, snapshot: Record<string, number>): void {
    const m = PROFESSIONS[up.track];
    this.lvlEl.innerHTML =
      `<div class="pl-t">${itemIcon(m?.icon ?? '⚓')} ${m?.name ?? up.track} — Level ${up.level}!</div>`
      + `<div class="pl-p">${perkLine(up.track, snapshot[up.track] ?? 0)}</div>`;
    this.lvlEl.classList.remove('go'); void this.lvlEl.offsetWidth; this.lvlEl.classList.add('go');
    try { this.audio.coins(); } catch { /* audio not armed yet */ }
  }
}
